import { Check, X } from 'lucide-react';
import { useI18n } from '../../../i18n/useI18n';
import {
  getPasswordPolicyChecklist,
  type PasswordPolicyRule,
} from '../../../utils/passwordPolicyUtils';
import './PasswordPolicyChecklist.css';

export type PasswordPolicyKeyPrefix =
  'settings.password.policy' | 'lac.users.profile.resetPasswordPolicy';

type PasswordPolicyChecklistProps = {
  password: string;
  policyKeyPrefix: PasswordPolicyKeyPrefix;
  className?: string;
};

function translatePolicyRule(
  translate: (key: string) => string,
  prefix: PasswordPolicyKeyPrefix,
  rule: PasswordPolicyRule,
): string {
  switch (prefix) {
    case 'settings.password.policy':
      return translate(`settings.password.policy.${rule}`);
    case 'lac.users.profile.resetPasswordPolicy':
      return translate(`lac.users.profile.resetPasswordPolicy.${rule}`);
  }
}

export function PasswordPolicyChecklist({
  password,
  policyKeyPrefix,
  className,
}: PasswordPolicyChecklistProps) {
  const { translate } = useI18n();
  const checklist = getPasswordPolicyChecklist(password);

  return (
    <ul
      className={['password-policy-checklist', className]
        .filter(Boolean)
        .join(' ')}
      aria-live="polite"
    >
      {checklist.map(({ rule, met }) => (
        <li
          key={rule}
          className={[
            'password-policy-checklist__item',
            met
              ? 'password-policy-checklist__item--met'
              : 'password-policy-checklist__item--unmet',
          ].join(' ')}
        >
          <span className="password-policy-checklist__icon" aria-hidden>
            {met ? <Check strokeWidth={2.5} /> : <X strokeWidth={2.5} />}
          </span>
          <span>{translatePolicyRule(translate, policyKeyPrefix, rule)}</span>
        </li>
      ))}
    </ul>
  );
}
